import { useEffect, useRef, useState } from 'preact/hooks';
import { Sheet } from './ui';
import { isBookEan, toIsbn13 } from '../../shared/isbn';

// Scanner de cod de bare ISBN. Pe Android (Chrome/Samsung) folosește BarcodeDetector (instant, nativ);
// pe iPhone încarcă ZXing doar când e nevoie. Se poate scrie și ISBN-ul de mână.
export function IsbnScanner({ onCode, onClose }: { onCode: (isbn13: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const doneRef = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let zxingControls: { stop: () => void } | null = null;
    let cancelled = false;

    const found = (raw: string) => {
      if (doneRef.current) return;
      const code = raw.replace(/\D/g, '');
      if (!isBookEan(code)) return; // ignorăm codurile care nu sunt de carte (ex. preț)
      doneRef.current = true;
      navigator.vibrate?.(60);
      onCode(code);
    };

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } }, audio: false });
        if (cancelled) return;
        const v = videoRef.current!;
        v.srcObject = stream;
        await v.play();
        const BD = (window as any).BarcodeDetector;
        const formats: string[] = BD?.getSupportedFormats ? await BD.getSupportedFormats() : [];
        if (BD && formats.includes('ean_13')) {
          const det = new BD({ formats: ['ean_13'] });
          const loop = async () => {
            if (cancelled || doneRef.current) return;
            try { const codes = await det.detect(v); codes.forEach((c: any) => found(c.rawValue)); } catch {}
            raf = requestAnimationFrame(() => setTimeout(loop, 120) as any);
          };
          loop();
        } else {
          const { BrowserMultiFormatReader } = await import('@zxing/browser');
          const reader = new BrowserMultiFormatReader();
          zxingControls = await reader.decodeFromVideoElement(v, (res: any) => { if (res) found(res.getText()); });
        }
      } catch (e) {
        setErr('Camera nu poate fi pornită. Verifică permisiunea pentru cameră sau scrie ISBN-ul mai jos.');
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      zxingControls?.stop();
      stream?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const submitManual = () => {
    const i13 = toIsbn13(manual);
    if (!i13) { setErr('ISBN invalid — verifică cifrele (10 sau 13).'); return; }
    doneRef.current = true;
    onCode(i13);
  };

  return (
    <Sheet open onClose={onClose} title="Scanează codul de bare">
      <div class="relative overflow-hidden rounded-2xl bg-ink-900 aspect-[4/3]">
        <video ref={videoRef} class="h-full w-full object-cover" playsInline muted />
        <div class="pointer-events-none absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 rounded-xl border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,.35)]" />
        <div class="absolute bottom-2 inset-x-0 text-center text-xs text-white/90">Ține codul de bare (ISBN, 978…) în chenar</div>
      </div>
      {err && <p class="mt-3 text-sm text-red-700">{err}</p>}
      <div class="mt-4 flex gap-2">
        <input class="field" inputMode="numeric" placeholder="sau scrie ISBN-ul" value={manual} onInput={e => setManual((e.target as HTMLInputElement).value)}
          onKeyDown={e => { if (e.key === 'Enter') submitManual(); }} data-testid="isbn-manual" />
        <button class="btn-primary" onClick={submitManual}>Caută</button>
      </div>
    </Sheet>
  );
}
